import { Download, Loader2, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  SettingRow,
  SettingToggle,
  settingButtonClass,
} from '@/components/settings-page/setting-controls';
import { useDesktopUpdateStatus } from '@/hooks/use-desktop-update-status';
import {
  checkForDesktopUpdate,
  type DesktopUpdateStatus,
  desktopUpdateChannel,
  downloadDesktopUpdate,
  installDesktopUpdate,
  setDesktopUpdateChannel,
} from '@/lib/electron-bridge';

/**
 * The running version, marked while beta updates are on. Versions are CalVer either way (a beta
 * is a GitHub prerelease of the same format), and the cloud does not say which kind is running.
 */
export function versionLabel(version: string, beta: boolean): string {
  return beta ? `Version ${version} (beta)` : `Version ${version}`;
}

function actionLabel(status: DesktopUpdateStatus): string {
  switch (status.state) {
    case 'checking':
      return 'Checking…';
    case 'available':
      return 'Download update';
    case 'downloading':
      return `Downloading ${Math.round(status.percent)}%`;
    case 'ready':
      return 'Restart and update';
    case 'error':
      return 'Try again';
    default:
      return 'Check for updates';
  }
}

function statusDescription(status: DesktopUpdateStatus, beta: boolean): string {
  switch (status.state) {
    case 'unsupported':
      return `${versionLabel(status.currentVersion, beta)}. Updates are available in packaged macOS, Windows, and Linux AppImage builds.`;
    case 'idle':
      return `${versionLabel(status.currentVersion, beta)}. Crowdex checks for updates automatically.`;
    case 'checking':
      return `${versionLabel(status.currentVersion, beta)}. Looking for a newer release…`;
    case 'up-to-date':
      return `${versionLabel(status.currentVersion, beta)} is up to date.`;
    case 'available':
      return `Version ${status.version} is available. It will only be installed when you choose to restart.`;
    case 'downloading':
      return `Downloading version ${status.version} (${Math.round(status.percent)}%).`;
    case 'ready':
      return `Version ${status.version} is ready. Restart Crowdex to finish installing it.`;
    case 'error':
      return `Could not update Crowdex: ${status.message}`;
    case 'unlinked':
      return `${versionLabel(status.currentVersion, beta)}. Updates come from Crowdex Cloud: link it under Account to receive them.`;
  }
}

/** Whether the Desktop follows beta releases (the prerelease channel); null until it says. */
function useBetaUpdates(): [boolean | null, (beta: boolean) => void] {
  const [beta, setBeta] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    void desktopUpdateChannel().then(
      (channel) => {
        if (live) setBeta(channel === 'prerelease');
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);
  function choose(value: boolean) {
    setBeta(value);
    void setDesktopUpdateChannel(value ? 'prerelease' : 'stable').catch(() => setBeta(!value));
  }
  return [beta, choose];
}

export function DesktopUpdateSettings() {
  const nativeStatus = useDesktopUpdateStatus();
  const [localError, setLocalError] = useState<string | null>(null);
  const [beta, setBeta] = useBetaUpdates();
  const status: DesktopUpdateStatus | null = localError
    ? { state: 'error', currentVersion: nativeStatus?.currentVersion ?? '', message: localError }
    : nativeStatus;
  if (!status) return null;
  const busy = status.state === 'checking' || status.state === 'downloading';
  const unsupported = status.state === 'unsupported';

  async function runAction() {
    if (!status || busy || unsupported) return;
    setLocalError(null);
    try {
      if (status.state === 'available') await downloadDesktopUpdate();
      else if (status.state === 'ready') await installDesktopUpdate();
      else await checkForDesktopUpdate();
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : String(error));
    }
  }

  return (
    <>
      <SettingRow description={statusDescription(status, beta === true)} title="Updates">
        {!unsupported && (
          <button
            className={settingButtonClass()}
            disabled={busy}
            onClick={() => void runAction()}
            type="button"
          >
            {busy ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : status.state === 'available' ? (
              <Download className="size-3.5" />
            ) : (
              <RotateCcw className="size-3.5" />
            )}
            {actionLabel(status)}
          </button>
        )}
      </SettingRow>
      {beta !== null && (
        <SettingRow title="Beta updates">
          <SettingToggle checked={beta} label="Beta updates" onChange={setBeta} />
        </SettingRow>
      )}
    </>
  );
}
